import {
  Color3,
  Mesh,
  MeshBuilder,
  Scene,
  StandardMaterial,
  TransformNode,
} from '@babylonjs/core';
import type { RgbColor } from '@roadworld/shared';
import { applyInkOutline } from './outlines.js';
import { VehicleTuning } from './tuning.js';

/** Default chassis color (the original hardcoded red). */
export const DEFAULT_CHASSIS_COLOR: RgbColor = { r: 0.78, g: 0.16, b: 0.18 };

/**
 * Build a chassis visual (TransformNode hierarchy). Pure visuals — no physics.
 * Used both by the local Vehicle and by RemotePlayer for other players in the session.
 */
export function createChassisMesh(
  scene: Scene,
  T: typeof VehicleTuning,
  color: RgbColor = DEFAULT_CHASSIS_COLOR,
): TransformNode {
  const root = new TransformNode('chassis-visual', scene);

  const body = MeshBuilder.CreateBox(
    'chassis-body',
    {
      width: T.chassisWidth,
      height: T.chassisHeight,
      depth: T.chassisLength,
    },
    scene,
  );
  body.parent = root;
  body.position.y = 0;
  const bodyMat = new StandardMaterial('chassis-mat', scene);
  bodyMat.diffuseColor = new Color3(color.r, color.g, color.b);
  bodyMat.specularColor = new Color3(0.2, 0.2, 0.2);
  body.material = bodyMat;
  applyInkOutline(body);

  // Cabin "greenhouse" on top.
  const cabin = MeshBuilder.CreateBox(
    'chassis-cabin',
    {
      width: T.chassisWidth * 0.85,
      height: T.chassisHeight * 0.6,
      depth: T.chassisLength * 0.45,
    },
    scene,
  );
  cabin.parent = root;
  cabin.position.set(0, T.chassisHeight * 0.55, -T.chassisLength * 0.05);
  const cabinMat = new StandardMaterial('cabin-mat', scene);
  cabinMat.diffuseColor = new Color3(0.15, 0.16, 0.2);
  cabin.material = cabinMat;
  applyInkOutline(cabin);
  return root;
}

export function createWheelMesh(
  scene: Scene,
  T: typeof VehicleTuning,
  name: string,
): Mesh {
  const cyl = MeshBuilder.CreateCylinder(
    name,
    {
      diameter: T.wheelRadius * 2,
      height: T.wheelWidth,
      tessellation: 18,
    },
    scene,
  );
  cyl.rotation.z = Math.PI / 2;
  cyl.bakeCurrentTransformIntoVertices();
  const mat = new StandardMaterial(`${name}-mat`, scene);
  mat.diffuseColor = new Color3(0.07, 0.07, 0.07);
  cyl.material = mat;
  applyInkOutline(cyl, 0.02);
  return cyl;
}
